import React, { createContext, useContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { UserManager, WebStorageStateStore, User } from "oidc-client-ts";
import { CLIENT_ORDER } from "../config/clientConfig";
import { getPermissionsForRole, PERMISSIONS } from "../utils/permissionsConfig";

const IDLE_TIMEOUT_MS = (parseInt(process.env.REACT_APP_IDLE_TIMEOUT_MINUTES, 10) || 180) * 60 * 1000;
const IDLE_WARNING_MS = Math.min(5 * 60 * 1000, IDLE_TIMEOUT_MS * 0.25);

const AuthContext = createContext(null);

const region = process.env.REACT_APP_COGNITO_REGION;
const userPoolId = process.env.REACT_APP_COGNITO_USER_POOL_ID;
const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
const redirectUri = process.env.REACT_APP_COGNITO_REDIRECT_URI;
const logoutUri = process.env.REACT_APP_COGNITO_LOGOUT_URI || redirectUri;
const cognitoDomain = process.env.REACT_APP_COGNITO_DOMAIN;
const SCOPES = "openid email phone profile";

const authority = `https://cognito-idp.${region}.amazonaws.com/${userPoolId}`;

const userManager = new UserManager({
  authority,
  client_id: clientId,
  redirect_uri: redirectUri,
  response_type: "code",
  scope: SCOPES,
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  stateStore: new WebStorageStateStore({ store: window.localStorage }),
  automaticSilentRenew: false,
  loadUserInfo: false,
  filterProtocolClaims: true,
  clockSkew: 300,
});

// Broadcast channel so getIdToken() (called outside React) can notify
// AuthProvider of a token refresh, preventing a stale-closure double-refresh
// that would consume the rotation token twice and trigger a Cognito 403.
const _tokenRefreshChannel = new EventTarget();
let _refreshInFlight = null; // serialise concurrent refresh calls

async function refreshStoredUser(currentUser) {
  if (!currentUser?.refresh_token) throw new Error("No refresh token available");

  // Serialise concurrent refresh calls: if one is already in flight, wait for
  // it instead of firing a second request with the same (soon-to-be-rotated)
  // refresh token.  A double-consume of a rotation token causes Cognito to
  // invalidate the entire session and return HTTP 403 "explicit deny".
  if (_refreshInFlight) return _refreshInFlight;

  const doRefresh = async () => {
    const tokenEndpoint = `${cognitoDomain}/oauth2/token`;
    const body = new URLSearchParams({
      grant_type: "refresh_token",
      client_id: clientId,
      refresh_token: currentUser.refresh_token,
    });

    const resp = await fetch(tokenEndpoint, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body,
    });

    if (!resp.ok) throw new Error("Failed to refresh tokens");

    const data = await resp.json();
    const now = Math.floor(Date.now() / 1000);
    const expires_in = data.expires_in || currentUser.expires_in || 0;
    const expires_at = now + expires_in;

    const newUser = new User({
      id_token: data.id_token || currentUser.id_token,
      access_token: data.access_token,
      refresh_token: data.refresh_token || currentUser.refresh_token,
      token_type: "Bearer",
      scope: SCOPES,
      expires_at,
      profile: currentUser.profile,
      session_state: currentUser.session_state,
    });

    await userManager.storeUser(newUser);

    // Notify AuthProvider so it syncs React state and resets its own refresh
    // timer — preventing a stale-closure re-refresh with the already-consumed token.
    _tokenRefreshChannel.dispatchEvent(
      Object.assign(new Event("tokenRefreshed"), { newUser })
    );

    return newUser;
  };

  _refreshInFlight = doRefresh().finally(() => { _refreshInFlight = null; });
  return _refreshInFlight;
}

/** Role tier precedence for resolving conflicts (higher index = higher privilege). */
const ROLE_PRECEDENCE = ["viewer", "operator", "admin"];
const TRANSFORM_TEAM_GROUP = "TRANSFORM_TEAM";
const FULL_ADMIN_GROUP = "FULL_ADMIN";

/**
 * Cognito groups that grant access to the Transform dashboards
 * (KPI, QA, and Data Team). These are the only roles that may
 * view the leadership/operational metrics.
 */
export const TRANSFORM_DASHBOARD_GROUPS = Object.freeze([
  "TRANSFORM_TEAM",
  "FULL_OPERATOR",
  "FULL_VIEWER",
]);

/**
 * Returns true when the given user belongs to any of the
 * groups allowed to view the Transform dashboards.
 * @param {Object|null} user
 * @returns {boolean}
 */
export function hasTransformDashboardAccess(user) {
  if (!user) return false;
  const groups = Array.isArray(user.groups)
    ? user.groups.map((group) => String(group).toUpperCase())
    : [];
  return groups.some((group) => TRANSFORM_DASHBOARD_GROUPS.includes(group));
}

/**
 * Returns the higher-precedence role of the two.
 * @param {string} a
 * @param {string} b
 * @returns {string}
 */
function higherRole(a, b) {
  return ROLE_PRECEDENCE.indexOf(a) >= ROLE_PRECEDENCE.indexOf(b) ? a : b;
}

/**
 * Derives user information from the Cognito profile.
 *
 * Parses cognito:groups into a per-client role map and a flat permissions Set.
 *
 * Group formats recognised:
 *   - "{CLIENT}_{ROLE}"  e.g. CMIG_ADMIN, SIG_OPERATOR, PEEL_VIEWER  → clientRoles entry
 *   - "TRANSFORM_TEAM"                                  → admin on all 5 clients
 *   - "FULL_ADMIN"                                      → KPI settings access only
 *   - "FULL_OPERATOR"                                   → operator on all 5 clients
 *   - "FULL_VIEWER"                                     → viewer on all 5 clients
 *   - "AGENT_SMITH"                                     → feature flag (isAgentSmith)
 *   - anything else                                     → ignored
 *
 * @param {Object} profile - The user profile from Cognito ID token
 * @returns {Object|null} User object with username, name, email, groups,
 *   clientRoles, permissions, role, displayRole, allowedClients, isAgentSmith, isTransformTeam
 */
export function deriveUser(profile) {
  if (!profile) return null;

  const username = profile["cognito:username"] || profile.email || "user";
  const name = profile.name || username;
  const groups = (profile["cognito:groups"] || []).map((g) => g.toUpperCase());
  console.log(`[AuthContext] Derived user: ${username}, groups: ${groups.join(", ")}`);

  const clientRoles = {};
  let isAgentSmith = false;
  const isTransformTeam = groups.includes(TRANSFORM_TEAM_GROUP);
  const isFullAdmin = groups.includes(FULL_ADMIN_GROUP);

  for (const group of groups) {
    if (group === TRANSFORM_TEAM_GROUP) {
      // Super-admin: grant admin role on all known clients
      for (const client of CLIENT_ORDER) {
        clientRoles[client] = "admin";
      }
      continue;
    }

    if (group === FULL_ADMIN_GROUP) continue;

    if (group === "FULL_OPERATOR") {
      // Cross-client operator: grant operator on all clients (preserve higher role if already set)
      for (const client of CLIENT_ORDER) {
        clientRoles[client] = clientRoles[client]
          ? higherRole(clientRoles[client], "operator")
          : "operator";
      }
      continue;
    }

    if (group === "FULL_VIEWER") {
      // Cross-client viewer: grant viewer on all clients (preserve higher role if already set)
      for (const client of CLIENT_ORDER) {
        clientRoles[client] = clientRoles[client]
          ? higherRole(clientRoles[client], "viewer")
          : "viewer";
      }
      continue;
    }

    if (group === "AGENT_SMITH") {
      isAgentSmith = true;
      continue;
    }

    // Parse "{CLIENT}_{ROLE}" — e.g. CMIG_ADMIN, PEEL_VIEWER, PMIC_OPERATOR
    const underscoreIdx = group.lastIndexOf("_");
    if (underscoreIdx === -1) continue;

    const client = group.slice(0, underscoreIdx);
    const role = group.slice(underscoreIdx + 1).toLowerCase();

    if (!CLIENT_ORDER.includes(client)) continue;
    if (!ROLE_PRECEDENCE.includes(role)) continue;

    // Keep the highest role if the client appears in multiple groups
    clientRoles[client] = clientRoles[client]
      ? higherRole(clientRoles[client], role)
      : role;
  }

  // allowedClients preserves CLIENT_ORDER ordering
  const allowedClients = CLIENT_ORDER.filter((c) => clientRoles[c]);

  // permissions = union of all per-client role permissions
  const permissions = new Set();
  for (const role of Object.values(clientRoles)) {
    for (const perm of getPermissionsForRole(role)) {
      permissions.add(perm);
    }
  }

  // MOR preset is exclusively visible to TRANSFORM_TEAM — not exposed via any
  // role tier so no {CLIENT}_ADMIN or FULL_OPERATOR ever receives it.
  if (groups.includes("TRANSFORM_TEAM")) {
    permissions.add(PERMISSIONS.REPORT_BUILDER_MOR_VIEW);
    permissions.add(PERMISSIONS.REPORT_BUILDER_MOR_GENERATE);
    permissions.add(PERMISSIONS.REPORT_BUILDER_MOR_TEMPLATE_EDIT);
  }

  // role = highest role across all clients (for backward-compatible isAdmin checks)
  const highestRole = Object.values(clientRoles).reduce(
    (acc, r) => higherRole(acc, r),
    "viewer"
  ) || "viewer";
  const role = allowedClients.length > 0 ? highestRole : "viewer";

  // displayRole = human-readable label for the user card in the sidebar
  const displayRole = isFullAdmin
    ? "Full Admin"
    : isTransformTeam
      ? "Transform Team"
    : groups.includes("FULL_OPERATOR")
      ? "Full Operator"
      : groups.includes("FULL_VIEWER")
        ? "Full Viewer"
        : role.charAt(0).toUpperCase() + role.slice(1);

  return { username, name, email: profile.email, groups, clientRoles, permissions, role, displayRole, allowedClients, isAgentSmith, isTransformTeam, isFullAdmin };
}

export function AuthProvider({ children }) {
  const [oidcUser, setOidcUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [idleWarning, setIdleWarning] = useState(null); // null | 'warning' | 'extended'
  const idleTimersRef = useRef();
  const beforeLogoutRef = useRef([]);

  // Load user from storage on mount
  useEffect(() => {
    userManager
      .getUser()
      .then((u) => {
        if (u && !u.expired) {
          setOidcUser(u);
        }
      })
      .catch(() => {})
      .finally(() => {
        setLoading(false);
      });
  }, []);

  // Sync React state when getIdToken() refreshes the token outside React
  // (e.g. during a long-running agent call).  Without this the auto-refresh
  // timer would fire with the old oidcUser closure and consume an already-
  // rotated refresh token, causing Cognito to return HTTP 403.
  useEffect(() => {
    const onExternalRefresh = (ev) => {
      if (ev.newUser) setOidcUser(ev.newUser);
    };
    _tokenRefreshChannel.addEventListener("tokenRefreshed", onExternalRefresh);
    return () => _tokenRefreshChannel.removeEventListener("tokenRefreshed", onExternalRefresh);
  }, []);

  const isAuthenticated = !!oidcUser && !oidcUser.expired;
  const user = isAuthenticated ? deriveUser(oidcUser.profile) : null;
  const tokens = useMemo(
    () =>
      isAuthenticated
        ? {
            accessToken: oidcUser.access_token,
            idToken: oidcUser.id_token,
            refreshToken: oidcUser.refresh_token || null,
          }
        : null,
    [isAuthenticated, oidcUser]
  );

  const loginRedirect = () => userManager.signinRedirect();

  const handleCallback = async () => {
    try {
      const u = await userManager.signinCallback();
      
      if (u && !u.expired) {
        setOidcUser(u);
      }
      
      return u;
    } catch (error) {
      throw error;
    }
  };

  const registerBeforeLogout = useCallback((fn) => {
    beforeLogoutRef.current.push(fn);
    return () => {
      beforeLogoutRef.current = beforeLogoutRef.current.filter((f) => f !== fn);
    };
  }, []);

  const logout = async () => {
    // Run all registered cleanup callbacks (e.g. release chat locks)
    for (const fn of beforeLogoutRef.current) {
      try { await fn(); } catch (e) { console.error("[AuthContext] beforeLogout callback failed:", e); }
    }
    try {
      await userManager.removeUser();
    } catch {}
    setOidcUser(null);
    window.location.href = `${cognitoDomain}/logout?client_id=${encodeURIComponent(
      clientId
    )}&logout_uri=${encodeURIComponent(logoutUri)}`;
  };

  const refreshTokens = useCallback(async () => {
    const newUser = await refreshStoredUser(oidcUser);
    setOidcUser(newUser);

    return {
      accessToken: newUser.access_token,
      idToken: newUser.id_token,
      refreshToken: newUser.refresh_token,
    };
  }, [oidcUser]);

  // Automatically refresh tokens before they expire.
  // Reads expires_at from oidcUser to schedule, but at fire-time always fetches
  // the current user from userManager (source of truth) to avoid using a
  // stale-closure refresh token that may have already been rotated by
  // getIdToken() running outside React.
  useEffect(() => {
    if (!oidcUser) return;

    const THRESHOLD_SECONDS = 300; // Refresh 5 minutes before expiry
    const now = Math.floor(Date.now() / 1000);
    const exp = oidcUser.expires_at;

    if (!exp) return;

    let delaySeconds = exp - now - THRESHOLD_SECONDS;
    if (delaySeconds < 0) delaySeconds = 0;

    const timeout = setTimeout(async () => {
      // Always read the freshest user from storage — avoids consuming an
      // already-rotated refresh token captured in the closure.
      const currentUser = await userManager.getUser();
      if (!currentUser || currentUser.expired) {
        await logout();
        return;
      }
      // If an external refresh already bumped the expiry, skip (nothing to do)
      const stillNeedsRefresh =
        currentUser.expires_at - Math.floor(Date.now() / 1000) < THRESHOLD_SECONDS + 30;
      if (!stillNeedsRefresh) {
        setOidcUser(currentUser);
        return;
      }
      try {
        const newUser = await refreshStoredUser(currentUser);
        setOidcUser(newUser);
      } catch {
        const afterFail = await userManager.getUser();
        if (!afterFail || afterFail.expired) await logout();
      }
    }, delaySeconds * 1000);

    return () => clearTimeout(timeout);
  }, [oidcUser]);

  // Session timeout: show warning then logout via setTimeout
  const startIdleTimers = useCallback(() => {
    clearTimeout(idleTimersRef.current?.warn);
    clearTimeout(idleTimersRef.current?.out);
    idleTimersRef.current = {
      warn: setTimeout(() => setIdleWarning('warning'), IDLE_TIMEOUT_MS - IDLE_WARNING_MS),
      out:  setTimeout(() => { logout(); }, IDLE_TIMEOUT_MS),
    };
    setIdleWarning(null);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    startIdleTimers();
    return () => {
      clearTimeout(idleTimersRef.current?.warn);
      clearTimeout(idleTimersRef.current?.out);
    };
  }, [isAuthenticated, startIdleTimers]);

  // Treat document clicks as user activity and keep the idle window fresh.
  useEffect(() => {
    if (!isAuthenticated) return;
    const onActivity = () => {
      if (idleWarning === 'warning') {
        clearTimeout(idleTimersRef.current?.warn);
        clearTimeout(idleTimersRef.current?.out);
        setIdleWarning('extended');
        setTimeout(() => startIdleTimers(), 2000);
        return;
      }
      if (idleWarning !== 'extended') {
        startIdleTimers();
      }
    };
    document.addEventListener('click', onActivity, true);
    return () => document.removeEventListener('click', onActivity, true);
  }, [isAuthenticated, idleWarning, startIdleTimers]);

  const hasAccessToClient = useCallback((client) => {
    if (!user) return false;
    return user.allowedClients.includes(String(client).toUpperCase());
  }, [user]);

  /**
   * Checks whether the current user has a specific permission.
   *
   * When `client` is provided, the check is scoped to that client's role
   * (e.g. a CMIG_VIEWER has validations.view for CMIG but not validations.trigger).
   *
   * When `client` is omitted, the check uses the union permissions Set —
   * true if the user has the permission on *any* client.
   *
   * @param {string} perm - Permission key (e.g. "validations.trigger")
   * @returns {boolean}
   */
  const hasPermission = useCallback((perm, client) => {
    if (!user) return false;
    if (client && String(client).toLowerCase() !== 'all') {
      const role = user.clientRoles[String(client).toUpperCase()];
      if (!role) return false;
      return getPermissionsForRole(role).has(perm);
    }
    return user.permissions.has(perm);
  }, [user]);

  const isAdmin = user?.role === "admin";
  const isFullAdmin = user?.isFullAdmin ?? false;
  const isAgentSmith = user?.isAgentSmith ?? false;
  const isTransformTeam = user?.isTransformTeam ?? false;
  const hasTransformDashboard = useMemo(() => hasTransformDashboardAccess(user), [user]);

  const value = useMemo(
    () => ({
      isAuthenticated,
      loading,
      user,
      tokens,
      loginRedirect,
      logout,
      registerBeforeLogout,
      hasAccessToClient,
      isAdmin,
      isFullAdmin,
      isAgentSmith,
      isTransformTeam,
      hasTransformDashboard,
      handleCallback,
      refreshTokens,
      idleWarning,
      clientRoles: user?.clientRoles ?? {},
      permissions: user?.permissions ?? new Set(),
      hasPermission,
    }),
    [isAuthenticated, loading, user, tokens, isAdmin, isFullAdmin, isAgentSmith, isTransformTeam, hasTransformDashboard, idleWarning, hasAccessToClient, refreshTokens, registerBeforeLogout, hasPermission]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

/**
 * Gets the current ID token from storage (for use in services)
 * @returns {Promise<string|null>} The ID token or null if not authenticated
 */
/**
 * Current ID token, refreshed when expired.
 * minTtlSeconds: also refresh when the token expires within this many seconds
 * (callers that hand the token to long-running backend work).
 */
export function getIdToken({ minTtlSeconds = 0 } = {}) {
  return userManager.getUser()
    .then(async (user) => {
      const ttl = user?.expires_at ? user.expires_at - Math.floor(Date.now() / 1000) : 0;
      if (user && !user.expired && ttl >= minTtlSeconds) {
        return user.id_token;
      }
      if (user?.refresh_token) {
        try {
          const refreshedUser = await refreshStoredUser(user);
          return refreshedUser.id_token;
        } catch (error) {
          // Early refresh failed: the current token is still valid, use it.
          if (!user.expired) return user.id_token;
          throw error;
        }
      }
      return user && !user.expired ? user.id_token : null;
    })
    .catch((error) => {
      console.error('[AuthContext] Error getting ID token:', error);
      return null;
    });
}

